
import React from 'react';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getKitId, getScoreboardState, updateScoreboardState } from "@/api/scoreboardApi";
import { useScoreboardRealtime } from "./useScoreboardRealtime";
import { UpdateScoreboardState, defaultScoreboardState } from "@/types/scoreboard";
import { toast } from "sonner";

export function useScoreboardState(kitNumber?: string) {
  const queryClient = useQueryClient();

  // Get kit ID
  const { data: kit } = useQuery({
    queryKey: ['kit', kitNumber],
    queryFn: () => getKitId(kitNumber!),
    enabled: !!kitNumber,
  });

  // Get scoreboard state
  const { data: scoreboard } = useQuery({
    queryKey: ['scoreboard-state', kit?.id],
    queryFn: () => getScoreboardState(kit!.id),
    enabled: !!kit?.id,
  });

  // Setup real-time updates
  useScoreboardRealtime(kit?.id, queryClient);

  // Update mutation
  const mutation = useMutation({
    mutationFn: (newState: UpdateScoreboardState) => {
      if (!kit?.id) throw new Error('No kit ID available');
      return updateScoreboardState(kit.id, newState);
    },
    onError: (error) => {
      console.error('Error updating scoreboard:', error);
      toast.error('Failed to update scoreboard');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoreboard-state', kit?.id] });
    },
  });

  // Merge the default state with the scoreboard data, ensuring base_runners exists
  const state = {
    ...defaultScoreboardState,
    ...scoreboard,
    base_runners: scoreboard?.base_runners || defaultScoreboardState.base_runners
  };

  const handleBaseRunnerChange = (index: number) => {
    const newBaseRunners = [...state.base_runners];
    newBaseRunners[index] = !newBaseRunners[index];
    mutation.mutate({ base_runners: newBaseRunners });
  };

  const updateTeamInfo = (teamInfo: UpdateScoreboardState) => {
    mutation.mutate(teamInfo);
  };

  return {
    // State values
    teamAScore: state.team_a_score,
    teamBScore: state.team_b_score,
    teamAHits: state.team_a_hits,
    teamAErrors: state.team_a_errors,
    teamBHits: state.team_b_hits,
    teamBErrors: state.team_b_errors,
    balls: state.balls,
    strikes: state.strikes,
    outs: state.outs,
    count: state.inning,
    isTopInning: state.is_top_inning,
    baseRunners: state.base_runners,
    teamAName: state.team_a_name,
    teamAPrimaryColor: state.team_a_primary_color,
    teamASecondaryColor: state.team_a_secondary_color,
    teamALogoUrl: state.team_a_logo_url,
    teamBName: state.team_b_name,
    teamBPrimaryColor: state.team_b_primary_color,
    teamBSecondaryColor: state.team_b_secondary_color,
    teamBLogoUrl: state.team_b_logo_url,

    // Update functions
    setTeamAScore: (score: number) => mutation.mutate({ team_a_score: score }),
    setTeamBScore: (score: number) => mutation.mutate({ team_b_score: score }),
    setTeamAHits: (hits: number) => mutation.mutate({ team_a_hits: hits }),
    setTeamAErrors: (errors: number) => mutation.mutate({ team_a_errors: errors }),
    setTeamBHits: (hits: number) => mutation.mutate({ team_b_hits: hits }),
    setTeamBErrors: (errors: number) => mutation.mutate({ team_b_errors: errors }),
    setBalls: (balls: number) => mutation.mutate({ balls }),
    setStrikes: (strikes: number) => mutation.mutate({ strikes }),
    setOuts: (outs: number) => mutation.mutate({ outs }),
    setCount: (inning: number) => mutation.mutate({ inning }),
    setIsTopInning: (is_top_inning: boolean) => mutation.mutate({ is_top_inning }),
    resetCount: () => mutation.mutate({ balls: 0, strikes: 0 }),
    setBaseRunnerChange: handleBaseRunnerChange,
    updateTeamInfo,
  };
}
